import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/layout/Footer';
import { NamedGrayIcon } from '../../components/icons/GrayIconBox';
import '../../styles/design-system/index.css';

const AiImageGuidePage = () => {
  const navigate = useNavigate();

  const faqData = [
    { q: '색칠공부 도안을 AI로 만들 수 있나요?', a: '네, 왓퀴즈에서 "색칠공부" 용도를 선택하고 주제를 입력하면 흑백 색칠공부 도안이 자동 생성돼요. 초등 수업이나 방과후 활동에 바로 활용할 수 있어요.' },
    { q: '교실 포스터를 AI로 만들 수 있나요?', a: '네, "포스터" 용도를 선택하면 학급 규칙, 안전 수칙, 학습 가이드 등의 교실 포스터를 AI로 생성할 수 있어요.' },
    { q: '생성된 이미지의 해상도는 어떤가요?', a: '고해상도 이미지가 생성되므로, 인쇄용 포스터부터 PPT 삽입용까지 다양한 용도로 활용할 수 있어요.' },
    { q: '학급 단체사진을 만화풍으로 바꿀 수 있나요?', a: '네, "학급사진" 용도를 선택하고 단체사진을 업로드하면 귀여운 만화풍으로 변환돼요.' },
    { q: 'AI로 만든 이미지의 저작권은 어떻게 되나요?', a: '왓퀴즈에서 생성된 이미지는 교육 목적으로 자유롭게 사용할 수 있어요. 수업, 학습지, PPT 등에 활용하세요.' },
  ];

  return (
    <>
      <Helmet>
        <title>AI 이미지 만들기 가이드 | 색칠공부·포스터·플래시카드 생성 방법 (2026)</title>
        <meta name="description" content="교육용 이미지를 AI로 만드는 방법을 안내해요. 색칠공부 도안, 개념 다이어그램, 교실 포스터, 플래시카드 등 7가지 용도. 5가지 스타일 선택. 교사 전용 무료 AI 이미지 생성." />
        <meta name="keywords" content="AI 이미지 만들기, 수업 이미지 만들기, 색칠공부 만들기, 플래시카드 만들기, 교육용 이미지 AI, 교실 포스터 만들기, 인포그래픽 만들기, 교사용 이미지 생성, 수업자료 이미지, AI 이미지 생성 가이드" />
        <link rel="canonical" href="https://whatquiz.co.kr/guide/ai-image" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://whatquiz.co.kr/guide/ai-image" />
        <meta property="og:title" content="AI 이미지 만들기 가이드 | 색칠공부·포스터·플래시카드 생성 방법" />
        <meta property="og:description" content="교육용 이미지를 AI로 만드는 방법. 색칠공부, 포스터, 플래시카드 등 7가지 용도 안내." />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:locale" content="ko_KR" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              { "@type": "Article", "headline": "AI 이미지 만들기 가이드", "description": "교사가 AI로 교육용 이미지를 생성하는 실전 가이드", "author": { "@type": "Organization", "name": "왓퀴즈" }, "publisher": { "@type": "Organization", "name": "왓퀴즈", "logo": "https://whatquiz.co.kr/images/logo512.png" }, "datePublished": "2026-03-09", "dateModified": "2026-03-09" },
              { "@type": "FAQPage", "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) }
            ]
          })}
        </script>
      </Helmet>

      <div style={{ fontFamily: 'var(--whatquiz-font-family)', backgroundColor: '#fff', minHeight: '100vh' }}>
        <section style={{ padding: '80px 20px 60px', textAlign: 'center', background: 'linear-gradient(180deg, #fdf4ff 0%, #fff 100%)' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#f3e8ff', color: '#7c3aed', borderRadius: '20px', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>2026년 최신 가이드</span>
            <h1 style={{ fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: '800', color: '#111', lineHeight: '1.3', marginBottom: '20px' }}>교육용 이미지, AI로 만들기</h1>
            <p style={{ fontSize: '18px', color: '#64748b', lineHeight: '1.7', marginBottom: '32px' }}>
              AI 이미지 생성이란 텍스트 설명만으로 색칠공부 도안, 다이어그램, 포스터, 플래시카드 등 교육용 이미지를 자동으로 만드는 기능이에요.
              이 가이드에서 7가지 용도별 활용법과 5가지 스타일을 상세히 안내해요.
            </p>
            <button onClick={() => navigate('/ai-image')} style={{ padding: '14px 28px', background: '#7c3aed', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
              지금 바로 이미지 만들기 →
            </button>
          </div>
        </section>

        <article style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', marginBottom: '16px' }}>7가지 용도로 활용하세요</h2>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '24px' }}>
            왓퀴즈 AI 이미지 생성은 교사의 수업 상황에 맞는 7가지 용도를 제공해요. 각 용도에 맞는 비율과 스타일이 자동 적용돼요.
          </p>

          {[
            { icon: 'lightbulb', title: '개념 설명 다이어그램', desc: '과학의 광합성 과정, 수학의 도형 분류 등 추상적 개념을 시각적 다이어그램으로 표현해요. 16:9 비율로 PPT 슬라이드에 바로 삽입할 수 있어요.' },
            { icon: 'crayon', title: '색칠공부 도안', desc: '동물, 식물, 계절 등 다양한 주제의 흑백 색칠공부 도안을 생성해요. 3:4 세로 비율로 인쇄에 최적화되어 있어요. 초등 수업, 방과후 활동에 활용하세요.' },
            { icon: 'chart', title: '인포그래픽', desc: '역사 연표, 과학 분류, 사회 통계 등을 시각적으로 정리한 인포그래픽을 만들어요. 교실 게시판이나 학습 자료에 활용하기 좋아요.' },
            { icon: 'image', title: '교실 포스터', desc: '학급 규칙, 안전 수칙, 독서 목록 등 교실에 게시할 포스터를 디자인해요. 학기 초 교실 꾸미기에 유용해요.' },
            { icon: 'cards', title: '플래시카드', desc: '영어 단어, 한자, 과학 용어 등을 카드 형태로 만들어요. 4:3 가로 비율로 카드 인쇄에 적합해요.' },
            { icon: 'camera', title: '학급사진 만화풍 변환', desc: '학급 단체사진을 업로드하면 귀여운 만화풍으로 변환해요. 학급 홈페이지, 포스터 등에 활용하세요.' },
          ].map((item, idx) => (
            <div key={idx} style={{ padding: '20px 0', borderBottom: idx < 5 ? '1px solid #f1f5f9' : 'none' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}><NamedGrayIcon name={item.icon} boxSize={40} /> {item.title}</h3>
              <p style={{ fontSize: '15px', color: '#64748b', lineHeight: '1.7' }}>{item.desc}</p>
            </div>
          ))}

          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', marginBottom: '16px', marginTop: '48px' }}>5가지 스타일</h2>
          <p style={{ fontSize: '16px', color: '#334155', lineHeight: '1.8', marginBottom: '24px' }}>
            학년과 수업 분위기에 맞는 스타일을 선택하면 이미지의 전체적인 느낌이 달라져요.
          </p>
          <ul style={{ fontSize: '15px', color: '#334155', lineHeight: '2.2', paddingLeft: '20px', marginBottom: '40px' }}>
            <li><strong>선명한:</strong> 또렷하고 깨끗한 교육용 이미지. 중·고등학교 수업에 적합</li>
            <li><strong>귀여운:</strong> 초등학생이 좋아하는 캐릭터풍. 저학년 수업에 적합</li>
            <li><strong>수채화:</strong> 부드럽고 감성적인 수채화 느낌. 미술, 국어 수업에 어울림</li>
            <li><strong>미니멀:</strong> 깔끔하고 군더더기 없는 디자인. 학습자료, 인포그래픽에 적합</li>
            <li><strong>사실적:</strong> 실물에 가까운 사실적 표현. 과학, 사회 수업에 적합</li>
          </ul>

          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', marginBottom: '24px', marginTop: '48px' }}>자주 묻는 질문</h2>
          {faqData.map((faq, idx) => (
            <div key={idx} style={{ borderBottom: '1px solid #e2e8f0', padding: '20px 0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111', marginBottom: '8px' }}>{faq.q}</h3>
              <p style={{ fontSize: '15px', color: '#64748b', lineHeight: '1.7' }}>{faq.a}</p>
            </div>
          ))}
        </article>

        <section style={{ padding: '60px 20px', textAlign: 'center', background: 'linear-gradient(180deg, #fdf4ff 0%, #fff 100%)' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#111', marginBottom: '12px' }}>수업 이미지, AI로 바로 만들어보세요</h2>
          <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '24px' }}>교사 인증 후 무료로 시작할 수 있어요</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/ai-image')} style={{ padding: '14px 28px', background: '#7c3aed', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>AI 이미지 만들기</button>
            <button onClick={() => navigate('/guide/ai-teacher-tools')} style={{ padding: '14px 28px', background: '#fff', color: '#334155', fontSize: '16px', fontWeight: '600', border: '1px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer' }}>교사 AI 도구 총정리</button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AiImageGuidePage;
